import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, ErrorAlert, PageHeader, Spinner } from '../components/ui';

export function AiProvidersPage() {
  const { data: providers } = useAsync(() => api.providers(), []);
  const models = useAsync(() => api.aiModels(), []);
  const roles = useAsync(() => api.aiRoles(), []);
  const [testModel, setTestModel] = useState<string | null>(null);
  const [testOut, setTestOut] = useState<Record<string, string> | null>(null);
  const [testError, setTestError] = useState<unknown>(null);

  const test = async (provider: string, model: string) => {
    setTestModel(`${provider}/${model}`);
    setTestOut(null);
    setTestError(null);
    try {
      const res = await api.aiGenerate({ task: 'excerpt', input: `Return "OK" as a short confirmation.`, provider, model });
      setTestOut({ provider: res.provider, model: res.model, latency: `${res.latencyMs}ms`, cost: `$${res.estimatedCost.toFixed(6)}` });
    } catch (err) {
      setTestError(err);
    } finally {
      setTestModel(null);
    }
  };

  return (
    <>
      <PageHeader title="AI Providers" subtitle="AI Router: task+quality → rola → provider/model. Modele legacy są oznaczane i nie dostają nowych zadań." />
      <ErrorAlert error={testError} />

      {providers && (
        <Card title="Providers / Publisherzy">
          <div className="pill-list">
            {providers.ai.providers.map((p) => (
              <Badge key={p.key} tone={p.configured ? 'success' : 'warning'}>
                {p.key}: {p.configured ? 'configured' : 'mock fallback'}
              </Badge>
            ))}
          </div>
        </Card>
      )}

      <div style={{ height: 16 }} />

      {roles.loading && <Spinner />}
      {roles.data && (
        <Card title="Role routing (task → rola → primary)" flush>
          <table className="table">
            <thead>
              <tr>
                <th>Rola</th>
                <th>Primary</th>
                <th>Fallbacki</th>
                <th>Timeout</th>
                <th>Max koszt</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(roles.data.roles).map((r) => (
                <tr key={r.role}>
                  <td style={{ fontWeight: 550 }}>{r.role}</td>
                  <td className="mono">{r.primary.provider}/{r.primary.model}</td>
                  <td className="mono">{r.fallbacks.map((f) => `${f.provider}/${f.model}`).join(', ') || '—'}</td>
                  <td>{r.timeoutMs}ms</td>
                  <td>${r.maxCostUsd}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <div style={{ height: 16 }} />

      {models.loading && <Spinner />}
      {models.error ? <ErrorAlert error={models.error} /> : null}
      {models.data && (
        <Card title={`Model registry (${models.data.catalog.length})`} flush>
          <table className="table">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Model</th>
                <th>Typ</th>
                <th>Capabilities</th>
                <th>Koszt (in/out)</th>
                <th>Legacy</th>
                <th style={{ textAlign: 'right' }}>Test</th>
              </tr>
            </thead>
            <tbody>
              {models.data.catalog.map((m) => (
                <tr key={m.key}>
                  <td style={{ fontWeight: 550 }}>{m.provider}</td>
                  <td className="mono">{m.model}</td>
                  <td>
                    <Badge tone="neutral">{m.kind}</Badge>
                  </td>
                  <td className="muted" style={{ fontSize: 11 }}>{m.capabilities.join(', ')}</td>
                  <td className="muted">${m.priceInput} / ${m.priceOutput} / 1M</td>
                  <td>
                    <Badge tone={m.legacy ? 'danger' : 'success'}>{m.legacy ? 'legacy' : 'current'}</Badge>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {m.kind === 'TEXT' && (
                      <Button size="sm" disabled={testModel === `${m.provider}/${m.model}`} onClick={() => test(m.provider, m.model)}>
                        {testModel === `${m.provider}/${m.model}` ? '…' : 'Test'}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {testOut && (
            <div className="alert alert--success" style={{ margin: '12px 16px' }}>
              Test OK: {testOut.provider}/{testOut.model} · {testOut.latency} · {testOut.cost}
            </div>
          )}
        </Card>
      )}
    </>
  );
}